import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Users, ShieldCheck, UserCheck, Plus, Loader2, Ticket, RefreshCw, Trash2, Power, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";

const randomCode = () => Array.from({ length: 8 }, () => "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"[Math.floor(Math.random() * 32)]).join("");

function Stat({ icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4" data-testid={testid}>
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent-hover)]">{icon}</div>
      <div>
        <p className="text-2xl font-bold leading-none text-[var(--text-primary)]" data-testid={`${testid}-value`}>{value}</p>
        <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">{label}</p>
      </div>
    </div>
  );
}

function TierBadge({ full }) {
  return (
    <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${full ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>
      {full ? "Full Access" : "Free"}
    </span>
  );
}

export default function UserManagement() {
  const { user } = useAuth();
  const [data, setData] = useState({ counts: { total: 0, free: 0, full: 0 }, users: [], pending: [] });
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState("");
  const [busy, setBusy] = useState("");
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState({ code: randomCode(), max_redemptions: 0, expires_at: "", active: true });
  const [creating, setCreating] = useState(false);
  const [welcome, setWelcome] = useState({ count: null, total_full_access: null });
  const [sending, setSending] = useState(false);
  const [emailResults, setEmailResults] = useState(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [u, c, w] = await Promise.all([api.get("/admin/users"), api.get("/admin/invite-codes"), api.get("/admin/welcome-emails/pending")]);
      setData(u.data);
      setCodes(c.data.codes);
      setWelcome(w.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error(formatApiError(e.response?.data?.detail) || "Could not load users.");
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user && user.is_admin) load(); }, [user, load]);

  if (user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const setAccessFor = async (email, full_access) => {
    setBusy(email);
    try {
      await api.post("/admin/access", { email, full_access });
      toast.success(full_access ? `Full Access granted to ${email}` : `Full Access revoked for ${email}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(""); }
  };

  const addEmail = async () => {
    const e = emailInput.trim().toLowerCase();
    if (!e || !e.includes("@")) { toast.error("Enter a valid email"); return; }
    await setAccessFor(e, true);
    setEmailInput("");
  };

  const createCode = async () => {
    if (!form.code.trim()) { toast.error("Enter a code string."); return; }
    setCreating(true);
    try {
      await api.post("/admin/invite-codes", {
        code: form.code.trim(),
        max_redemptions: parseInt(form.max_redemptions, 10) || 0,
        expires_at: form.expires_at || null,
        active: form.active,
      });
      toast.success("Invite code created");
      setForm({ code: randomCode(), max_redemptions: 0, expires_at: "", active: true });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setCreating(false); }
  };

  const updateCode = async (id, patch, msg) => {
    setBusy(id);
    try { await api.put(`/admin/invite-codes/${id}`, patch); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };

  const rotateCode = (id) => updateCode(id, { code: randomCode() }, "Code rotated");
  const toggleCode = (c) => updateCode(c.id, { active: !c.active }, c.active ? "Code deactivated" : "Code activated");
  const deleteCode = async (id) => {
    if (!window.confirm("Delete this invite code?")) return;
    setBusy(id);
    try { await api.delete(`/admin/invite-codes/${id}`); toast("Code deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(""); }
  };

  const sendWelcomeEmails = async () => {
    const n = welcome.count || 0;
    if (!n) { toast("Everyone with Full Access has already received the welcome email."); return; }
    if (!window.confirm(`This will email ${n} Full Access ${n === 1 ? "user" : "users"} who haven't received the welcome email yet. Send now?`)) return;
    setSending(true);
    setEmailResults(null);
    try {
      const { data } = await api.post("/admin/welcome-emails/backfill");
      setEmailResults(data.results || []);
      if (data.sent > 0 && data.failed === 0) {
        toast.success(`Resend accepted ${data.sent} ${data.sent === 1 ? "email" : "emails"}.`);
      } else if (data.sent > 0) {
        toast.error(`Resend accepted ${data.sent}, rejected ${data.failed}. See details below.`);
      } else {
        toast.error(`Resend accepted 0 emails. See the error for each recipient below.`);
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send welcome emails.");
    } finally { setSending(false); }
  };

  const { counts, users, pending } = data;

  const q = filter.trim().toLowerCase();
  const filteredUsers = q ? users.filter((u) => (u.email || "").toLowerCase().includes(q)) : users;
  const selectableEmails = filteredUsers.filter((u) => !u.is_admin).map((u) => u.email);
  const allSelected = selectableEmails.length > 0 && selectableEmails.every((e) => selected.has(e));
  const someSelected = selectableEmails.some((e) => selected.has(e));

  const toggleOne = (email) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(email) ? next.delete(email) : next.add(email);
    return next;
  });

  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allSelected) selectableEmails.forEach((e) => next.delete(e));
    else selectableEmails.forEach((e) => next.add(e));
    return next;
  });

  const doDelete = async () => {
    const emails = confirmTarget?.emails || [];
    if (!emails.length) return;
    setDeleting(true);
    try {
      const { data: res } = await api.delete("/admin/users", { data: { emails } });
      toast.success(`Deleted ${res.deleted} ${res.deleted === 1 ? "account" : "accounts"}`);
      setSelected((prev) => {
        const next = new Set(prev);
        emails.forEach((e) => next.delete(e));
        return next;
      });
      setConfirmTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    } finally { setDeleting(false); }
  };

  const confirmCount = confirmTarget?.emails?.length || 0;

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8 lg:px-8" data-testid="user-management-page">
        <h1 className="font-head text-4xl text-[var(--text-primary)]">User Management</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">See everyone who has signed up and grant or revoke Full Access.</p>

        {/* Counts */}
        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="user-counts">
          <Stat icon={<Users size={18} />} label="Total Users" value={loading ? "—" : counts.total} testid="count-total" />
          <Stat icon={<UserCheck size={18} />} label="Free Tier" value={loading ? "—" : counts.free} testid="count-free" />
          <Stat icon={<ShieldCheck size={18} />} label="Full Access" value={loading ? "—" : counts.full} testid="count-full" />
        </div>

        {/* Welcome emails */}
        <section className="mt-8" data-testid="welcome-emails-section">
          <h2 className="font-head text-2xl text-[var(--text-primary)]">Welcome emails</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">New Full Access sign-ups get the welcome email automatically. Use this to back-fill anyone who joined before it existed — each person is only ever emailed once, and only after Resend confirms it.</p>
          {welcome.key_present === false && (
            <p className="mt-3 rounded-[var(--card-radius)] border border-red-500/40 bg-red-500/10 px-4 py-2.5 text-sm text-red-300" data-testid="welcome-key-warning">
              ⚠ RESEND_API_KEY is not set in this environment — sends will be rejected. Add the live key in Secrets first.
            </p>
          )}
          {welcome.key_present && (
            <p className="mt-3 text-xs text-[var(--text-secondary)]" data-testid="welcome-key-info">
              Resend key detected: <span className="font-mono text-[var(--text-primary)]">{welcome.key_prefix}</span> ({welcome.key_length} chars{welcome.looks_like_resend ? "" : " — ⚠ doesn't start with re_"}) · from <span className="font-mono text-[var(--text-primary)]">{welcome.from_field}</span>
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-4 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--accent)]/15 text-[var(--accent-hover)]"><Mail size={18} /></div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-sm text-[var(--text-primary)]" data-testid="welcome-pending-text">
                {welcome.count == null ? "—" : welcome.count === 0
                  ? "All Full Access users have already been welcomed."
                  : `${welcome.count} Full Access ${welcome.count === 1 ? "user hasn't" : "users haven't"} been emailed yet.`}
              </p>
            </div>
            <button onClick={sendWelcomeEmails} disabled={sending || loading || !welcome.count}
              className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-5 py-2.5 font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-50"
              data-testid="send-welcome-emails-button">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
              {sending ? "Sending…" : welcome.count ? `Email ${welcome.count} ${welcome.count === 1 ? "user" : "users"}` : "Nothing to send"}
            </button>
          </div>
          {emailResults && emailResults.length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="welcome-results">
              {emailResults.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-xs" data-testid={`welcome-result-${i}`}>
                  <span className={`font-bold uppercase ${r.status === "sent" ? "text-green-400" : r.status === "skipped" ? "text-[var(--text-secondary)]" : "text-red-400"}`}>{r.status}</span>
                  <span className="font-mono text-[var(--text-primary)]">{r.email}</span>
                  {r.status === "sent" && <span className="text-[var(--text-secondary)]">→ Resend id <span className="font-mono text-[var(--accent-hover)]">{r.id}</span></span>}
                  {r.status === "failed" && <span className="text-red-300">→ {r.error}{r.status_code ? ` (HTTP ${r.status_code})` : ""}</span>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Pre-approve an email */}
        <section className="mt-8" data-testid="preapprove-section">
          <h2 className="font-head text-2xl text-[var(--text-primary)]">Pre-approve an email</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Add an email that hasn't registered yet. Full Access is applied automatically the moment they sign up.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addEmail()} placeholder="email@example.com"
              className="min-w-[240px] flex-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="preapprove-email-input" />
            <button onClick={addEmail} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-5 py-2.5 font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]" data-testid="preapprove-add-button">
              <Plus size={16} /> Add Email
            </button>
          </div>

          {pending.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Pending (not yet registered) · {pending.length}</h3>
              <div className="mt-2 space-y-2" data-testid="pending-list">
                {pending.map((p) => (
                  <div key={p.email} className="flex flex-wrap items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3" data-testid={`pending-row-${p.email}`}>
                    <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-primary)]">{p.email}</span>
                    <span className="rounded bg-[var(--text-muted)]/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Pending</span>
                    <TierBadge full={p.full_access} />
                    <button onClick={() => setAccessFor(p.email, !p.full_access)} disabled={busy === p.email}
                      className="inline-flex items-center gap-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-semibold text-[var(--text-primary)] hover:border-[var(--accent)] disabled:opacity-50" data-testid={`pending-toggle-${p.email}`}>
                      {busy === p.email && <Loader2 size={12} className="animate-spin" />} {p.full_access ? "Revoke" : "Grant"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Invite codes */}
        <section className="mt-8" data-testid="invite-codes-section">
          <h2 className="font-head text-2xl text-[var(--text-primary)]">Invite codes</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Create shareable codes that unlock Full Access. Set a custom string, a redemption limit (0 = unlimited), and an optional expiry.</p>

          <div className="mt-4 grid gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4 sm:grid-cols-[1.4fr_0.8fr_1fr_auto] sm:items-end" data-testid="invite-create-form">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Code</label>
              <div className="flex gap-1">
                <input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} className="w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-sm uppercase tracking-wider text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="invite-code-input" />
                <button type="button" onClick={() => setForm((f) => ({ ...f, code: randomCode() }))} className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-2 text-[var(--text-secondary)] hover:text-[var(--accent)]" title="Generate" data-testid="invite-generate"><RefreshCw size={15} /></button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Max uses</label>
              <input type="number" min="0" value={form.max_redemptions} onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))} className="w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="invite-max-input" placeholder="0 = ∞" />
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Expires</label>
              <input type="date" value={form.expires_at} onChange={(e) => setForm((f) => ({ ...f, expires_at: e.target.value }))} className="w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="invite-expiry-input" />
            </div>
            <button onClick={createCode} disabled={creating} className="inline-flex items-center justify-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-5 py-2.5 font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="invite-create-button">
              {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create
            </button>
          </div>

          {codes.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="invite-codes-list">
              {codes.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3" data-testid={`invite-row-${c.id}`}>
                  <span className="flex items-center gap-2 font-mono text-sm font-bold tracking-wider text-[var(--text-primary)]"><Ticket size={15} className="text-[var(--accent-hover)]" />{c.code}</span>
                  <span className="text-xs text-[var(--text-secondary)]" data-testid={`invite-usage-${c.id}`}>
                    {c.used} used{c.max_redemptions ? ` · ${c.remaining} left` : " · unlimited"}
                  </span>
                  {c.expires_at && <span className={`text-xs ${c.expired ? "text-red-400" : "text-[var(--text-muted)]"}`}>{c.expired ? "expired " : "expires "}{c.expires_at}</span>}
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${c.active && !c.expired ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>{c.active ? "Active" : "Inactive"}</span>
                  <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => toggleCode(c)} disabled={busy === c.id} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-semibold text-[var(--text-primary)] hover:border-[var(--accent)] disabled:opacity-50" data-testid={`invite-toggle-${c.id}`}><Power size={12} /> {c.active ? "Deactivate" : "Activate"}</button>
                    <button onClick={() => rotateCode(c.id)} disabled={busy === c.id} className="rounded p-2 text-[var(--text-secondary)] hover:text-[var(--accent)]" title="Rotate code" data-testid={`invite-rotate-${c.id}`}><RefreshCw size={15} /></button>
                    <button onClick={() => deleteCode(c.id)} disabled={busy === c.id} className="rounded p-2 text-[var(--text-secondary)] hover:text-red-500" title="Delete" data-testid={`invite-delete-${c.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Registered users */}
        <section className="mt-8" data-testid="registered-users-section">
          <h2 className="font-head text-2xl text-[var(--text-primary)]">Registered users <span className="text-[var(--text-muted)]">({counts.total})</span></h2>

          {/* Filter + bulk actions */}
          {!loading && users.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <div className="relative min-w-[240px] flex-1">
                <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter by email (e.g. example.com)"
                  className="w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] py-2.5 pl-9 pr-3 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]"
                  data-testid="user-filter-input"
                />
              </div>
              {selected.size > 0 && (
                <button
                  onClick={() => setConfirmTarget({ mode: "bulk", emails: [...selected] })}
                  className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-red-600 px-4 py-2.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-red-700"
                  data-testid="bulk-delete-button">
                  <Trash2 size={15} /> Delete selected ({selected.size})
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="mt-6 flex items-center gap-2 text-[var(--text-secondary)]"><Loader2 size={16} className="animate-spin" /> Loading…</div>
          ) : users.length === 0 ? (
            <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="no-users">No users yet.</p>
          ) : (
            <div className="mt-4 overflow-hidden rounded-[var(--card-radius)] border border-[var(--border-subtle)]" data-testid="users-table">
              <div className="hidden bg-[var(--bg-surface)] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] sm:grid sm:grid-cols-[auto_2fr_1fr_1fr_auto] sm:items-center sm:gap-3">
                <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }} onChange={toggleAll}
                  disabled={selectableEmails.length === 0} className="h-4 w-4 cursor-pointer accent-[var(--accent)]" data-testid="select-all-checkbox" aria-label="Select all" />
                <span>Email</span><span>Signed up</span><span>Tier</span><span className="text-right">Action</span>
              </div>
              {filteredUsers.length === 0 ? (
                <p className="border-t border-[var(--border-subtle)] bg-[var(--bg-card)] px-4 py-6 text-center text-sm text-[var(--text-secondary)]" data-testid="no-filtered-users">No users match “{filter}”.</p>
              ) : filteredUsers.map((u) => (
                <div key={u.email} className="grid grid-cols-1 gap-2 border-t border-[var(--border-subtle)] bg-[var(--bg-card)] px-4 py-3 sm:grid-cols-[auto_2fr_1fr_1fr_auto] sm:items-center sm:gap-3" data-testid={`user-row-${u.email}`}>
                  <div className="flex items-center">
                    {u.is_admin ? (
                      <span className="inline-block h-4 w-4" aria-hidden="true" />
                    ) : (
                      <input type="checkbox" checked={selected.has(u.email)} onChange={() => toggleOne(u.email)}
                        className="h-4 w-4 cursor-pointer accent-[var(--accent)]" data-testid={`user-select-${u.email}`} aria-label={`Select ${u.email}`} />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[var(--text-primary)]">{u.email}</p>
                    {u.is_admin && <span className="mt-0.5 inline-block rounded bg-[var(--accent)]/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[var(--accent-hover)]">Admin</span>}
                  </div>
                  <span className="text-xs text-[var(--text-secondary)]" data-testid={`user-signup-${u.email}`}>{(u.created_at || "").slice(0, 10) || "—"}</span>
                  <span><TierBadge full={u.full_access} /></span>
                  <div className="flex items-center gap-2 sm:justify-end">
                    {u.is_admin ? (
                      <span className="text-xs text-[var(--text-muted)]">Always full</span>
                    ) : (
                      <>
                        <button onClick={() => setAccessFor(u.email, !u.full_access)} disabled={busy === u.email}
                          className={`inline-flex items-center gap-1 rounded-[var(--card-radius)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide disabled:opacity-50 ${u.full_access ? "border border-[var(--border-subtle)] text-[var(--text-primary)] hover:border-[var(--accent)]" : "bg-[var(--accent)] text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)]"}`}
                          data-testid={`user-toggle-${u.email}`}>
                          {busy === u.email && <Loader2 size={12} className="animate-spin" />} {u.full_access ? "Revoke" : "Approve"}
                        </button>
                        <button onClick={() => setConfirmTarget({ mode: "single", emails: [u.email] })}
                          className="rounded p-2 text-[var(--text-secondary)] hover:text-red-500" title="Delete account" data-testid={`user-delete-${u.email}`}>
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <AlertDialog open={!!confirmTarget} onOpenChange={(o) => { if (!o && !deleting) setConfirmTarget(null); }}>
        <AlertDialogContent className="border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-primary)]" data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[var(--text-primary)]">
              {confirmCount > 1 ? `Delete ${confirmCount} accounts?` : "Delete this account?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-secondary)]">
              {confirmCount > 1
                ? `This permanently removes the users and all their data (lists, favorites, friends). This cannot be undone.`
                : "This permanently removes the user and all their data (lists, favorites, friends). This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700" data-testid="delete-confirm-button">
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} className="mr-1" />}
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
